import { createContext, useCallback, useContext, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [opts, setOpts] = useState(null);
  const [busy, setBusy] = useState(false);

  const confirm = useCallback((options) => {
    return new Promise((resolve) => {
      setOpts({
        title: "Are you sure?",
        message: "",
        confirmLabel: "Confirm",
        cancelLabel: "Cancel",
        destructive: false,
        ...options,
        _resolve: resolve,
      });
    });
  }, []);

  const close = (result) => {
    if (opts?._resolve) opts._resolve(result);
    setOpts(null);
    setBusy(false);
  };

  const onConfirm = async () => {
    if (opts?.onConfirm) {
      setBusy(true);
      try {
        await opts.onConfirm();
        close(true);
      } catch {
        setBusy(false);
      }
    } else {
      close(true);
    }
  };

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {opts && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="confirm-dialog"
        >
          <div className="bento-card w-full max-w-md p-6 space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${
                    opts.destructive
                      ? "bg-rose-400/15 text-rose-300"
                      : "bg-electric/15 text-electric"
                  }`}
                >
                  <AlertTriangle size={18} />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase">{opts.title}</h3>
                  {opts.message && (
                    <div className="text-sm text-white/60 mt-1 leading-relaxed">{opts.message}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => close(false)}
                className="text-white/40 hover:text-white shrink-0"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>
            {opts.detailItems && opts.detailItems.length > 0 && (
              <div className="bg-white/[0.02] border border-white/8 rounded-md p-3 max-h-40 overflow-y-auto text-xs text-white/70 space-y-1">
                {opts.detailItems.map((item, i) => (
                  <div key={i} className="font-mono">• {item}</div>
                ))}
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => close(false)}
                className="btn-ghost text-xs"
                data-testid="confirm-cancel"
                disabled={busy}
              >
                {opts.cancelLabel}
              </button>
              <button
                onClick={onConfirm}
                disabled={busy}
                className={`text-xs inline-flex items-center gap-2 px-4 py-2 rounded-md font-display font-bold uppercase tracking-wider disabled:opacity-50 ${
                  opts.destructive
                    ? "bg-rose-500 hover:bg-rose-400 text-white"
                    : "bg-electric hover:bg-electric/90 text-black"
                }`}
                data-testid="confirm-submit"
              >
                {busy ? "Working…" : opts.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used within ConfirmProvider");
  return ctx;
}
